import { type RefObject, useEffect, useRef, useState } from "react";

import { useLatestCallback } from "@/hooks/useLatestCallback.ts";
import { createLogger } from "@/logger";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider.tsx";
import { InactivityMonitorService } from "@/services/InactivityMonitorService.ts";
import { readLastForcedReload, resolveIdleUpdateAction, writeLastForcedReload } from "@/utils/idleUpdateGate.ts";

const log = createLogger("version");

/** How often the gate is asked again while a build waits, so the idle trigger fires within this of the threshold. */
const CHECK_INTERVAL_MS = 15_000;

/** A storage that refuses every call, standing in when `sessionStorage` itself cannot be reached. */
const REFUSING_STORAGE: Pick<Storage, "getItem" | "setItem"> = {
  getItem: () => {
    throw new Error("sessionStorage unavailable");
  },
  setItem: () => {
    throw new Error("sessionStorage unavailable");
  },
};

function sessionStorageOrRefusing(): Pick<Storage, "getItem" | "setItem"> {
  try {
    return window.sessionStorage;
  } catch {
    return REFUSING_STORAGE;
  }
}

/**
 * Applies a waiting build without the Update button: at once after a cold or warm boot, otherwise once the user has
 * been idle for `web.idleUpdate.idleMinutes` (WEB-2417).
 *
 * The mount is the cold boot. A warm boot is the tab coming back (thawed, restored from the back/forward cache, back
 * online, or visible) after `resumeGapMinutes` without input: it asks the registration for a new worker at once,
 * since the 30s poll's timers were frozen, and opens a boot window of `bootWindowSeconds`.
 *
 * @param needRefresh - A new service worker is waiting.
 * @param registrationRef - The service worker registration, once registered.
 * @param applyUpdate - Activates the waiting worker and reloads the tab.
 */
export function useAutoApplyUpdate(
  needRefresh: boolean,
  registrationRef: RefObject<ServiceWorkerRegistration | undefined>,
  applyUpdate: () => void,
): void {
  const { idleUpdate } = useWebSettings().settings;
  const { enabled, idleMinutes, resumeGapMinutes, bootWindowSeconds, reloadGuardSeconds } = idleUpdate;

  const [monitor, setMonitor] = useState<InactivityMonitorService | null>(null);
  const bootWindowEndsAtRef = useRef(Date.now() + bootWindowSeconds * 1000);
  const appliedRef = useRef(false);

  useEffect(() => {
    const next = new InactivityMonitorService(idleMinutes * 60_000);
    next.start();
    setMonitor(next);
    return () => next.stop();
  }, [idleMinutes]);

  const evaluate = useLatestCallback(() => {
    if (appliedRef.current || !monitor) return;
    const storage = sessionStorageOrRefusing();
    const now = Date.now();
    const action = resolveIdleUpdateAction({
      needRefresh,
      enabled,
      isIdle: monitor.isIdle,
      bootWindowOpen: now < bootWindowEndsAtRef.current,
      lastForcedReload: readLastForcedReload(storage),
      reloadGuardSeconds,
      now,
    });
    if (action !== "apply" || !writeLastForcedReload(storage, now)) return;

    appliedRef.current = true;
    log.info("Applying the waiting build", { idle: monitor.isIdle, inactivityMs: monitor.inactivityDurationMs });
    applyUpdate();
  });

  const onComeBack = useLatestCallback(() => {
    if (!enabled || !monitor || monitor.inactivityDurationMs < resumeGapMinutes * 60_000) return;

    bootWindowEndsAtRef.current = Date.now() + bootWindowSeconds * 1000;
    registrationRef.current
      ?.update()
      .catch((error) => log.warn("Update check on resume failed", { error: String(error) }));
    evaluate();
  });

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") onComeBack();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) onComeBack();
    };

    document.addEventListener("resume", onComeBack);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onComeBack);
    return () => {
      document.removeEventListener("resume", onComeBack);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onComeBack);
    };
  }, [onComeBack]);

  useEffect(() => {
    if (!needRefresh || !enabled || !monitor) return;

    evaluate();
    const interval = setInterval(evaluate, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [needRefresh, enabled, monitor, evaluate]);
}
